
import { Link } from "react-router-dom";
import AccentBar from "./AccentBar";
import BrandLogo from "./BrandLogo";
import EditableText from "./EditableText";

const footerLinks = [
  {
    label: "Legal Notice",
    href: "https://www.dantz-ip.com/en/impressum",
  },
  {
    label: "Data Protection",
    href: "https://www.dantz-ip.com/en/data-protection",
  },
  {
    label: "FAQ",
    href: "/faq",
  },
  {
    label: "Contact",
    href: "https://www.dantz-ip.com/en/contact-and-make-appointment",
  },
  {
    label: "Terms & Conditions",
    href: "https://www.dantz-ip.com/en/terms",
  },
];

const secondColumnStart = "62%";

const DEFAULT_ADDRESS_1 = [
  "THE XPRESS IP COMPANY AG",
  "Bundesplatz 9",
  "6302 Zug",
  "Switzerland",
];

const DEFAULT_EMAIL = "info@xpress-ip.com";
const DEFAULT_TAGLINE = "EUROPEAN IP FILING, MADE SIMPLE.";
const DEFAULT_TAGLINE_SUB = "Patents · Trademarks · Designs";

interface FooterProps {
  editable?: boolean;
  address1?: string[];
  onSaveAddress1?: (index: number, value: string) => void;
  email?: string;
  tagline?: string;
  taglineSub?: string;
  onSaveEmail?: (value: string) => void;
  onSaveTagline?: (value: string) => void;
  onSaveTaglineSub?: (value: string) => void;
}

export default function Footer({
  editable = false,
  address1 = DEFAULT_ADDRESS_1,
  onSaveAddress1,
  email = DEFAULT_EMAIL,
  tagline = DEFAULT_TAGLINE,
  taglineSub = DEFAULT_TAGLINE_SUB,
  onSaveEmail,
  onSaveTagline,
  onSaveTaglineSub,
}: FooterProps) {
  return (
    <footer className="mt-auto bg-[#1c4263] px-4 py-8 text-sm text-[#aeb1b7] md:px-8">
      <div className="relative mx-auto max-w-6xl pt-6">
        <AccentBar
          position="top"
          tickOffsets={["0%", secondColumnStart]}
        />

        <Link
          to="/home"
          className="mt-6 block w-fit"
          aria-label="Go to dashboard"
        >
          <BrandLogo
            size="sm"
            className="text-white transition-colors hover:bg-white/10"
          />
        </Link>

        <div className="mt-8 grid grid-cols-1 gap-y-8 md:grid-cols-[62%_1fr] md:gap-y-0">
          <div className="flex flex-col gap-6 md:pr-8">
            {address1.length > 0 && (
              <div>
                {address1.map((line, index) => (
                  <EditableText
                    key={index}
                    as="p"
                    value={line}
                    editMode={editable}
                    onSave={(value) => onSaveAddress1?.(index, value)}
                  />
                ))}
              </div>
            )}

            {editable ? (
              <EditableText
                as="p"
                value={email}
                editMode
                onSave={(value) =>
                  onSaveEmail?.(value)
                }
              />
            ) : (
              <a
                href={`mailto:${email}`}
                className="hover:underline"
              >
                {email}
              </a>
            )}

            <div className="pt-2 text-white">
              <EditableText
                as="p"
                value={tagline}
                editMode={editable}
                onSave={(value) =>
                  onSaveTagline?.(value)
                }
                className="tracking-wide"
              />

              <EditableText
                as="p"
                value={taglineSub}
                editMode={editable}
                onSave={(value) =>
                  onSaveTaglineSub?.(value)
                }
                className="text-xs text-[#aeb1b7]"
              />
            </div>
          </div>

          <div className="flex flex-col gap-5">
            {footerLinks.map((link) =>
              link.href.startsWith("/") ? (
                <Link
                  key={link.label}
                  to={link.href}
                  className="hover:underline"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  {link.label}
                </a>
              )
            )}
          </div>
        </div>

        <div className="relative mt-8 text-center text-xs text-slate-300">
          <AccentBar position="bottom" rightTick />
        </div>
      </div>
    </footer>
  );
}
